import { useEffect } from 'react'
import L from 'leaflet'

// 對應 leaflet-src.js 內部 var START = Browser.touch ? 'touchstart mousedown' : 'mousedown'
// （模組私有變數，未對外匯出，只能依同樣邏輯自行重建）
const START_EVENT = L.Browser.touch ? 'touchstart mousedown' : 'mousedown'

// 手機直向時，畫面用 CSS transform: rotate(90deg) 強制轉成橫向顯示，但瀏覽器原生的
// pointer 事件座標不會跟著轉，導致 Leaflet 算出來的拖曳方向跟使用者視覺上的滑動方向對不上。
//
// 先前嘗試在 DOM 層攔截、重新 dispatch PointerEvent 給 Leaflet 的做法沒有成功
// （Leaflet 在 document 上也掛了 capture 階段的全域 listener 追蹤多指觸控狀態，
// 事件冒泡/捕獲的先後順序與是否正確覆寫存在不確定性，難以透過程式碼審查保證生效）。
//
// 改為直接 monkey-patch L.Draggable 內部負責讀取指標座標的兩個方法
// （_onDown 記錄拖曳起點、_onMove 計算每次移動的位移量，見
// node_modules/leaflet/dist/leaflet-src.js 的 Draggable.prototype）：
// 在原本的方法真正讀取 clientX/clientY 之前，把座標依 90 度旋轉做 X/Y 互換 + 鏡像。
//
// 這樣做之後 debug log 顯示 _onDown 的 patch 從未被呼叫（只有 _onMove 有觸發）：
// 根因是 Draggable.enable()（地圖初始化時就執行過）內部執行
//   on(this._dragStartTarget, START, this._onDown, this)
// L.DomEvent.on() 在呼叫當下就把「當時的 this._onDown」函式引用直接綁定到原生
// mousedown/touchstart 監聽器上，不是每次觸發都動態讀取 this._onDown。所以事後
// 覆寫 draggable._onDown 屬性完全不影響已經綁定好的原生事件監聽器，實際被呼叫的
// 永遠是原始未轉換座標的 _onDown。_onMove 則是在（原始）_onDown 內部才動態用
// on(document, ..., this._onMove, this) 訂閱，這時讀到的才是我 patch 過的版本 ——
// 於是 _startPoint 停留在未轉換的原始座標系，_onMove 卻用轉換後的座標系計算，
// 兩者不一致，從第一幀就產生巨大偏移，造成「拖曳一開始就被推向固定一側」的現象。
//
// 解法：不能只覆寫 draggable._onDown 屬性，必須先用 L.DomEvent.off 解除原生監聽器
// 上綁定的舊函式引用，再用 L.DomEvent.on 重新綁定 patch 過的版本，讓 _startPoint
// 與 _onMove 用的是同一套座標轉換。
export function useRotatedMapDrag(map, { enabled }) {
  useEffect(() => {
    if (!enabled || !map) return undefined

    const draggable = map.dragging?._draggable
    if (!draggable) return undefined

    const remapPoint = (p) => ({ clientX: p.clientY, clientY: -p.clientX })

    const remapEvent = (e) => {
      if (e.touches && e.touches.length) {
        const touches = []
        for (let i = 0; i < e.touches.length; i++) {
          touches.push(remapPoint(e.touches[i]))
        }
        return { type: e.type, touches, shiftKey: e.shiftKey, which: e.which, button: e.button, target: e.target }
      }
      return { type: e.type, ...remapPoint(e), shiftKey: e.shiftKey, which: e.which, button: e.button, target: e.target }
    }

    const originalOnDown = draggable._onDown.bind(draggable)

    const patchedOnDown = (e) => {
      originalOnDown(remapEvent(e))
      // rotate(90deg) 造成的視覺旋轉不是真的 CSS scale，把 Leaflet 算錯的縮放比例修正回 1:1
      draggable._parentScale = { x: 1, y: 1 }
    }

    // 解除原生監聽器上綁定的舊 _onDown 引用，換成會做座標轉換的版本，
    // 確保 _startPoint 跟 _onMove 用的是同一套（已轉換的）座標系
    L.DomEvent.off(draggable._dragStartTarget, START_EVENT, draggable._onDown, draggable)
    draggable._onDown = patchedOnDown
    L.DomEvent.on(draggable._dragStartTarget, START_EVENT, draggable._onDown, draggable)

    const originalOnMove = draggable._onMove.bind(draggable)
    draggable._onMove = (e) => originalOnMove(remapEvent(e))

    return () => {
      L.DomEvent.off(draggable._dragStartTarget, START_EVENT, draggable._onDown, draggable)
      draggable._onDown = originalOnDown
      L.DomEvent.on(draggable._dragStartTarget, START_EVENT, draggable._onDown, draggable)
      draggable._onMove = originalOnMove
    }
  }, [map, enabled])
}
